'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Container } from '@/components/primitives/Container/Container';
import { IBaseComponentProps } from '@/lib/types';
import { IFiltersCategory, IFilterCards as IFilterCardEntry, IFilterTags } from '@/.generated';
import { filterCardsVariants } from './FilterCards.styles';
import { ButtonWrapper } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper';
import { getCSLPAttributes } from '@/utils/type-guards';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { trackAdobeClick } from '@/utils/adobe';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';

const MOBILE_BREAKPOINT = 768;
// Default icon dimensions matching FSD spec (127×127); overridden by CMS asset dimensions
const DEFAULT_ICON_SIZE = 127;
// Fallback page sizes used when the CMS field is missing/empty/0
const DEFAULT_CARDS_PER_PAGE_DESKTOP = 8;
const DEFAULT_CARDS_PER_PAGE_MOBILE = 8;

export interface FilterCardsClientProps extends IBaseComponentProps {
  layout?: 'full-column' | 'two-column' | null;
  filter_category?: IFiltersCategory[];
  cards: IFilterCardEntry[];
  desktop_pagination_count?: number;
  mobile_pagination_count?: number;
  inline_navigation_id?: string;
  _metadata?: { uid?: string };
}

export const FilterCardsClient = (props: FilterCardsClientProps) => {
  const {
    componentName,
    _metadata,
    layout = 'two-column',
    filter_category = [],
    cards = [],
    desktop_pagination_count,
    mobile_pagination_count,
    inline_navigation_id,
  } = props;

  // Use CMS-driven page sizes, falling back to defaults when the value is missing or 0
  const desktopCardsPerPage = desktop_pagination_count || DEFAULT_CARDS_PER_PAGE_DESKTOP;
  const mobileCardsPerPage = mobile_pagination_count || DEFAULT_CARDS_PER_PAGE_MOBILE;

  const { globalLabels } = useGlobalLabels();
  const filterLabel = globalLabels?.filter_labels?.filter || 'Filter';
  const displayingResultsLabel =
    globalLabels?.filter_labels?.displaying_of_results ||
    'Displaying {start}-{end} of {total} results';

  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  const styles = filterCardsVariants({ layout: layout ?? 'two-column' });

  // Component identifier shared by every Click event fired from this component
  const clickLocation = `${componentName}_${_metadata?.uid}`;
  // Fire a Click event for a filter/pagination interaction in this component;
  // clickUrl mirrors the static clickType
  const trackClick = (clickName: string, clickType: string) =>
    trackAdobeClick({ clickName, clickType, clickLocation, clickUrl: clickType });

  // Track viewport width to switch cards-per-page between mobile and desktop
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Pre-select filter tags from URL query params (e.g. ?customertypetips=homeowner&customerinterest=air-quality)
  useEffect(() => {
    if (typeof window === 'undefined' || !filter_category?.length) return;
    const params = new URLSearchParams(window.location.search);
    if (!Array.from(params.keys()).length) return;

    // Lowercase + strip non-alphanumerics so "Air Quality", "air-quality", and "air_quality" all match
    const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

    // Find a tag inside a single category by matching the normalized value against its title or uid
    const findTagInCategory = (category: IFiltersCategory, normalizedValue: string) =>
      category.tags_reference?.find((tag) => {
        if (!tag.uid) return false;
        return (
          normalize(tag.tags_title || '') === normalizedValue ||
          normalize(tag.uid) === normalizedValue
        );
      });

    // Fallback: scan every category when the param key doesn't map to any known category
    const findTagAcrossCategories = (normalizedValue: string) => {
      for (const category of filter_category) {
        const tag = findTagInCategory(category, normalizedValue);
        if (tag) return tag;
      }
      return undefined;
    };

    const preselected = new Set<string>();
    const unmatched: { key: string; value: string; reason: string }[] = [];
    params.forEach((rawValue, rawKey) => {
      const normalizedKey = normalize(rawKey);
      if (!normalizedKey) return;

      // Match param key against category title OR uid (CMS title may differ from the URL convention)
      const matchedCategory = filter_category.find((cat) => {
        const candidates = [cat.category_title, cat.title, cat.uid].filter(Boolean) as string[];
        return candidates.some((candidate) => normalize(candidate) === normalizedKey);
      });

      const normalizedValues = rawValue
        .split(',')
        .map((value) => normalize(value))
        .filter(Boolean);

      normalizedValues.forEach((normalizedValue) => {
        const matchedTag = matchedCategory
          ? findTagInCategory(matchedCategory, normalizedValue)
          : findTagAcrossCategories(normalizedValue);
        if (matchedTag?.uid) {
          preselected.add(matchedTag.uid);
        } else {
          unmatched.push({
            key: rawKey,
            value: normalizedValue,
            reason: matchedCategory
              ? `value "${normalizedValue}" did not match any tag title/uid in category "${matchedCategory.category_title || matchedCategory.title}"`
              : `key "${rawKey}" did not match any category title/uid, and value "${normalizedValue}" did not match any tag in any category`,
          });
        }
      });
    });

    if (preselected.size > 0) setSelectedTags(preselected);
    // Run once on mount; filter_category is stable for a given page render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardsPerPage = useMemo(
    () => (isMobile ? mobileCardsPerPage : desktopCardsPerPage),
    [isMobile, desktopCardsPerPage, mobileCardsPerPage]
  );

  // OR within a category, AND across categories. Featured entries always float to top.
  const filteredCards = useMemo(() => {
    const featuredCards = cards.filter((card) => card.featured_entry_);
    const nonFeaturedCards = cards.filter((card) => !card.featured_entry_);

    if (selectedTags.size === 0) return [...featuredCards, ...nonFeaturedCards];

    // Collect selected tag uids per category; drop categories with no active selections
    const selectionsByCategory = filter_category
      .map((category) =>
        (category.tags_reference || [])
          .map((tag) => tag.uid)
          .filter((uid): uid is string => !!uid && selectedTags.has(uid))
      )
      .filter((selections) => selections.length > 0);

    // All selected tags belonged to empty/unknown categories — treat as no filter
    if (selectionsByCategory.length === 0) return [...featuredCards, ...nonFeaturedCards];

    // A card passes when every active category contributes at least one matching tag
    const cardMatches = (card: IFilterCardEntry) => {
      const cardTagUids = new Set((card.tags_reference || []).map((tag) => tag.uid));
      return selectionsByCategory.every((selections) =>
        selections.some((uid) => cardTagUids.has(uid))
      );
    };

    // Featured cards always show at top; non-featured only shown when they match the active filter
    const nonFeaturedFiltered = nonFeaturedCards.filter(cardMatches);
    return [...featuredCards, ...nonFeaturedFiltered];
  }, [cards, selectedTags, filter_category]);

  const totalPages = Math.ceil(filteredCards.length / cardsPerPage);
  const startIndex = (currentPage - 1) * cardsPerPage;
  const endIndex = Math.min(startIndex + cardsPerPage, filteredCards.length);
  const paginatedCards = filteredCards.slice(startIndex, endIndex);

  const resultsText = useMemo(() => {
    if (filteredCards.length === 0)
      return globalLabels?.filter_labels?.no_results_found_text || '0 results match your filters';
    return displayingResultsLabel
      .replace('{start}', String(startIndex + 1))
      .replace('{end}', String(endIndex))
      .replace('{total}', String(filteredCards.length));
  }, [displayingResultsLabel, startIndex, endIndex, filteredCards.length]);

  // Toggle a tag on/off and reset to page 1
  const handleTagToggle = useCallback((tagUid: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagUid)) {
        next.delete(tagUid);
      } else {
        next.add(tagUid);
      }
      return next;
    });
    setCurrentPage(1);
  }, []);

  // Clear all active filters and reset to page 1
  const handleClearSelections = useCallback(() => {
    setSelectedTags(new Set());
    setCurrentPage(1);
  }, []);

  // Jump to a page and scroll to the top of this component (not the page)
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    componentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleMobileFilterOpen = useCallback(() => {
    setIsMobileModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const handleMobileFilterClose = useCallback(() => {
    setIsMobileModalOpen(false);
    document.body.style.overflow = '';
  }, []);

  // Clear all filters and close the mobile modal
  const handleMobileClearAndClose = useCallback(() => {
    handleClearSelections();
    handleMobileFilterClose();
  }, [handleClearSelections, handleMobileFilterClose]);

  // Build inline style from CMS-driven color fields
  const getTagStyle = (tag: IFilterTags) => {
    const bgColor = tag.bg_color?.color_list || '';
    const textColor = tag.text_color?.color_list || '';
    return { backgroundColor: bgColor, color: textColor };
  };

  const renderFilterContent = () => (
    <>
      {filter_category?.map((category, catIndex) => (
        <div key={category.uid || catIndex} className={styles.filterCategory()}>
          <h3
            className={styles.filterCategoryTitle()}
            {...getCSLPAttributes(category.$?.category_title)}
          >
            {category.category_title || category.title}
          </h3>
          <div className={styles.filterCheckboxList()}>
            {category.tags_reference?.map((tag, tagIndex) => {
              const tagUid = tag.uid || `tag-${catIndex}-${tagIndex}`;
              const isChecked = selectedTags.has(tagUid);
              return (
                <label key={tagUid} className={styles.filterCheckboxLabel()}>
                  <input
                    type="checkbox"
                    className={styles.filterCheckbox()}
                    checked={isChecked}
                    onChange={() => {
                      trackClick(tag.tags_title || tagUid, 'filter-click');
                      handleTagToggle(tagUid);
                    }}
                    aria-label={tag.tags_title}
                  />
                  <span {...getCSLPAttributes(tag.$?.tags_title)}>{tag.tags_title}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <nav className={styles.paginationWrapper()} aria-label="Pagination">
        {currentPage > 1 && (
          <button
            type="button"
            className={styles.paginationButton()}
            onClick={() => {
              trackClick('Previous', 'filter-page-click');
              handlePageChange(currentPage - 1);
            }}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <i className="fa-sharp fa-solid fa-angle-left"></i>
          </button>
        )}
        {pages.map((page, index) =>
          typeof page === 'number' ? (
            <button
              key={index}
              type="button"
              className={`${styles.paginationButton()} ${page === currentPage ? styles.paginationButtonActive() : styles.pages()
                }`}
              onClick={() => {
                trackClick(`Page ${page}`, 'filter-page-click');
                handlePageChange(page);
              }}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          ) : (
            <span key={index} className={styles.paginationEllipsis()}>
              {page}
            </span>
          )
        )}
        {currentPage < pages?.length && (
          <button
            type="button"
            className={styles.paginationButton()}
            onClick={() => {
              trackClick('Next', 'filter-page-click');
              handlePageChange(currentPage + 1);
            }}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <i className="fa-sharp fa-solid fa-angle-right"></i>
          </button>
        )}
      </nav>
    );
  };

  if (!cards || cards.length === 0) return null;
  return (
    <Container
      containerBleed
      componentName={componentName}
      metaId={_metadata?.uid}
      id={inline_navigation_id}
    >
      <div ref={componentRef} className={styles.wrapper()}>
        {/* Desktop filter sidebar — hidden on mobile */}
        <aside className={styles.filterSidebar()} aria-label="Filter options">
          <div className={styles.filterHeader()}>
            <div className="flex items-center gap-1.25">
              <i className={`fa-regular fa-sliders ${styles.filterIcon()}`} aria-hidden="true" />
              <h4 className={styles.filterTitle()}>{filterLabel}</h4>
              {selectedTags.size > 0 && <span>({selectedTags.size})</span>}
            </div>
            {selectedTags.size > 0 && (
              <button
                type="button"
                className={styles.clearSelectionsButton()}
                onClick={() => {
                  trackClick('Clear Filter', 'filter-clear-click');
                  handleClearSelections();
                }}
                aria-label="Clear all filter selections"
              >
                Clear Filter
              </button>
            )}
          </div>
          {renderFilterContent()}
        </aside>

        {/* div, not main: the page layout already renders the single main landmark */}
        <div className={styles.mainContent()}>
          {/* Mobile filter trigger — visible only below lg breakpoint */}
          <button
            type="button"
            className={styles.mobileFilterButton()}
            onClick={() => {
              trackClick(filterLabel, 'filter-open');
              handleMobileFilterOpen();
            }}
            aria-label="Open filter options"
          >
            <i className="fa-regular fa-sliders" aria-hidden="true" />
            {filterLabel}
            {selectedTags.size > 0 && <span>({selectedTags.size})</span>}
          </button>

          <div className={styles.resultsHeader()}>
            <span className={styles.resultsCount()}>{resultsText}</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className={styles.cardGrid()}>
              {/* {paginatedCards.length === 0 && (
                <p className="text-gray-600 col-span-full text-center py-8">
                  No cards match your selected filters. Try adjusting your selections.
                </p>
              )} */}
              {paginatedCards.map((card, index) => {
                const visibleTags = card.display_tags_in_card_
                  ? card.tags_reference?.filter(
                    (tag) => tag.uid && tag.bg_color?.color_list && tag.text_color?.color_list
                  )
                  : [];
                // Mirror ButtonWrapper's isLink logic: direct link OR page reference
                const hasCta =
                  !!card.card_cta?.cta?.link?.href ||
                  !!card.card_cta?.cta?.page_reference?.[0]?.url;

                return (
                  <article key={card.uid || index} className={styles.card()}>
                    <div className={styles.cardTopBar()} aria-hidden="true" />
                    <div className={styles.cardBody()}>
                      {card.icon?.url && (
                        <div className={styles.cardImageWrapper()} aria-hidden="true">
                          <ImageWrapper
                            image={toEnhancedImage(card.icon, {
                              fallbackWidth: DEFAULT_ICON_SIZE,
                              fallbackHeight: DEFAULT_ICON_SIZE,
                              cslp: card.$?.icon,
                            })}
                            alt=""
                          />
                        </div>
                      )}
                      <div className={styles.cardContent()}>
                        {visibleTags && visibleTags.length > 0 && (
                          <div className={styles.cardTags()}>
                            {visibleTags?.map((tag, tagIdx) => (
                              <span
                                key={tag.uid || tagIdx}
                                className={styles.cardTag()}
                                style={getTagStyle(tag)}
                                {...getCSLPAttributes(tag.$?.tags_title)}
                              >
                                {tag.tags_title}
                              </span>
                            ))}
                          </div>
                        )}
                        {card.card_title && (
                          <h2
                            data-featured={card?.featured_entry_}
                            className={styles.cardTitle()}
                            {...getCSLPAttributes(card.$?.card_title)}
                          >
                            {card.card_title}
                          </h2>
                        )}
                        <RichTextWrapper
                          className={styles.cardDescription()}
                          content={card?.card_description}
                          {...getCSLPAttributes(card.$?.card_description)}
                        />
                      </div>
                      {hasCta && (
                        <div className={styles.cardCtaWrapper()}>
                          {/* variant + size are sourced from card.card_cta.cta.cta_variant / cta_size in CMS via ButtonWrapper */}
                          <ButtonWrapper cta={card.card_cta!.cta} />
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {renderPagination()}
        </div>
      </div>

      {/* Full-screen mobile filter modal */}
      {isMobileModalOpen && (
        <div
          className={styles.mobileModal()}
          role="dialog"
          aria-modal="true"
          aria-label="Filter options"
        >
          <div className={styles.mobileModalHeader()}>
            <h2 className={styles.mobileModalTitle()}>
              {filterLabel}
              {selectedTags.size > 0 && <span className="ml-1.75">({selectedTags.size})</span>}
            </h2>
            <button
              type="button"
              className={styles.mobileModalCloseButton()}
              onClick={() => {
                trackClick('Close', 'filter-close');
                handleMobileClearAndClose();
              }}
              aria-label="Close filter options"
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          </div>
          <div className={styles.mobileModalContent()}>{renderFilterContent()}</div>
          <div className={styles.mobileModalFooter()}>
            <button
              type="button"
              className={styles.applyFiltersButton()}
              onClick={() => {
                trackClick('Apply Filters', 'filter-apply');
                handleMobileFilterClose();
              }}
            >
              Apply Filters
            </button>
            <button
              type="button"
              className={styles.clearButton()}
              onClick={() => {
                trackClick('Clear Filter', 'filter-clear-click');
                handleClearSelections();
              }}
              aria-label="Clear all filters"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </Container>
  );
};
